import React, { createContext, useState, useContext, ReactNode } from "react";
import { setClipboard } from "common/clipboard/clipboard";
import { SimpleDialog } from "../dialogs/SimpleDialog";
import { useTranslation } from "react-i18next";
import { showToastSuccess } from "common/toast/toast";

interface ClipboardContextType {
  copyToClipboard: (text: string) => Promise<void>;
}

const ClipboardContext = createContext<ClipboardContextType | undefined>(
  undefined
);

export const useClipboard = (): ClipboardContextType => {
  const context = useContext(ClipboardContext);
  if (!context) {
    throw new Error(
      "useClipboard must be used within a ClipboardContextProvider"
    );
  }
  return context;
};

interface ClipboardContextProviderProps {
  children: ReactNode;
}

export const ClipboardContextProvider: React.FC<
  ClipboardContextProviderProps
> = ({ children }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clipboardText, setClipboardText] = useState("");

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await setClipboard(text);
      showToastSuccess(t("layout.copyLinks.linkCopied"));
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setClipboardText(text);
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <ClipboardContext.Provider value={{ copyToClipboard }}>
      {children}
      <SimpleDialog
        title={t("clipboard.copyFailed")}
        open={dialogOpen}
        onClose={handleDialogClose}
        hideButton
      >
        <div style={{ marginBottom: "16px" }}>
          {t("clipboard.cannotAccessClipboard")}
        </div>
        <div
          style={{
            padding: "12px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "#f5f5f5",
            wordBreak: "break-all",
          }}
        >
          {clipboardText}
        </div>
      </SimpleDialog>
    </ClipboardContext.Provider>
  );
};
